import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSliders, FiX, FiImage, FiMaximize } = FiIcons;

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (value: number) => void;
  hint?: string;
}

const Slider = ({ label, value, min, max, step, unit, onChange, hint }: SliderProps) => (
  <div className="space-y-2">
    <div className="flex justify-between items-center">
      <label htmlFor={`setting-${label.toLowerCase().replace(/\s+/g, '-')}`} className="text-sm font-bold text-slate-700 dark:text-slate-200">{label}</label>
      <span className="text-sm font-black text-indigo-600 dark:text-indigo-400 tabular-nums min-w-[48px] text-right">
        {value}{unit}
      </span>
    </div>
    <div className="relative h-5 flex items-center">
      <div className="absolute inset-x-0 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700" />
      <div
        className="absolute h-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
        style={{ width: `${((value - min) / (max - min)) * 100}%` }}
      />
      <input
        id={`setting-${label.toLowerCase().replace(/\s+/g, '-')}`}
        name={`setting-${label.toLowerCase().replace(/\s+/g, '-')}`}
        type="range"
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="relative w-full h-5 appearance-none bg-transparent cursor-pointer z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-indigo-500 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110"
      />
    </div>
    {hint && <p className="text-[11px] text-slate-400 leading-tight">{hint}</p>}
  </div>
);

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  quality: number;
  setQuality: (value: number) => void;
  resizePercent: number;
  setResizePercent: (value: number) => void;
}

const SettingsPanel = ({ isOpen, onClose, quality, setQuality, resizePercent, setResizePercent }: SettingsPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && e.target instanceof Node && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60]"
            onClick={onClose}
          />
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="absolute right-0 top-full mt-2 z-[90] w-[min(20rem,calc(100vw-2rem))] bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6 space-y-6"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="bg-indigo-500 p-2 rounded-xl text-white">
                  <SafeIcon icon={FiSliders} className="text-sm" />
                </div>
                <div>
                  <h3 className="font-black text-slate-800 dark:text-white text-sm leading-none">Settings</h3>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mt-1">Adjust Output</p>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close conversion settings"
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                type="button"
              >
                <SafeIcon icon={FiX} className="text-sm" />
              </button>
            </div>

            <div className="h-px bg-slate-100 dark:bg-slate-800" />

            <div className="space-y-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiImage} className="text-indigo-500 text-xs" />
                  <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">Compression</span>
                </div>
                <Slider
                  label="Image Quality" value={quality}
                  min={10} max={100} step={5} unit="%"
                  onChange={setQuality}
                  hint={quality >= 90 ? 'Maximum detail, larger files' : quality >= 70 ? 'Balanced quality and size' : 'High compression, smaller files'}
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiMaximize} className="text-purple-500 text-xs" />
                  <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">Dimensions</span>
                </div>
                <Slider
                  label="Scale Factor" value={resizePercent}
                  min={10} max={200} step={5} unit="%"
                  onChange={setResizePercent}
                  hint={resizePercent === 100 ? 'Keep original dimensions' : resizePercent > 100 ? `Upscale to ${resizePercent}%` : `Downscale to ${resizePercent}%`}
                />
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800 rounded-2xl p-4 border border-slate-100 dark:border-slate-700">
              <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                <strong className="text-slate-700 dark:text-slate-300">Note:</strong> Quality affects{' '}
                <span className="text-indigo-500 font-bold">JPG</span> &{' '}
                <span className="text-indigo-500 font-bold">WEBP</span>. PNG is always lossless.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SettingsPanel;
